/**
 * API client — Valora360 Platform.
 * Centraliza chamadas ao backend FastAPI que, por sua vez, conversa com o Supabase.
 *
 * IMPORTANTE: o frontend NUNCA chama o Supabase diretamente. Toda lógica
 * passa pelo backend para garantir segurança (chaves só ficam no .env do backend)
 * e consistência (mesma camada que aplicará Auth/RLS no Phase 2).
 */
const API_BASE = process.env.REACT_APP_BACKEND_URL;
if (!API_BASE) {
  // eslint-disable-next-line no-console
  console.warn("REACT_APP_BACKEND_URL não configurada.");
}

// Phase 12.55.11 — Anexa o Bearer do Supabase automaticamente em toda
// requisição. Sem esse header, endpoints protegidos (ex.: motor MGC-V360,
// endpoints /api/mgc/v360/*, /api/mgc/s360/*) rejeitavam com
// "Autenticação Bearer requerida" mesmo com o usuário logado.
async function _getBearer() {
  try {
    const mod = await import("@/lib/supabase");
    return await mod.getAccessToken().catch(() => null);
  } catch {
    return null;
  }
}

// Phase 12.57 — header de impersonação (modo ghost do Admin Geral). Import
// estático evitado aqui porque api.js é carregado antes do localStorage estar
// pronto em alguns entrypoints; a leitura é síncrona e barata.
function _impHeaders() {
  try {
    const raw = localStorage.getItem("imp_session");
    if (!raw) return {};
    const imp = JSON.parse(raw);
    if (!imp?.workspace_id) return {};
    if (imp.expires_at && new Date(imp.expires_at).getTime() < Date.now()) return {};
    return { "X-Imp-Workspace": imp.workspace_id };
  } catch {
    return {};
  }
}

async function request(path, opts = {}) {
  const url = `${API_BASE}/api${path}`;
  const { signal, ...rest } = opts;
  // Anexa Bearer se houver sessão. Endpoints públicos (login, setup-password)
  // funcionam do mesmo jeito: se getAccessToken devolve null, não anexa nada.
  let token = await _getBearer();
  const buildHeaders = (t) => ({
    "Content-Type": "application/json",
    ...(t ? { Authorization: `Bearer ${t}` } : {}),
    ..._impHeaders(),
    ...(rest.headers || {}),
  });
  let res;
  try {
    res = await fetch(url, { headers: buildHeaders(token), signal, ...rest });
  } catch (e) {
    // Normaliza erro de cancelamento — caller pode filtrar via e.name === 'AbortError'
    if (e.name === "AbortError") throw e;
    const err = new Error(e.message || "Erro de rede");
    err.cause = e;
    throw err;
  }
  // Phase 12.55.27 — Retry em 401: quando o Supabase acaba de rehidratar
  // (mudança de aba, tela após navegação em SPA long-idle), a primeira
  // chamada pode viajar sem Bearer válido. Forçamos refreshSession e
  // reenviamos UMA vez. Elimina "Sessão expirada" espúrio em endpoints
  // protegidos como /mgc/v360/motor/executar, /api/mgc/*.
  if (res.status === 401 && !opts.__isRetry) {
    try {
      const sbMod = await import("@/lib/supabase");
      const sb = await sbMod.getSupabase();
      const { data: refreshed } = await sb.auth.refreshSession();
      const newTok = refreshed?.session?.access_token || null;
      if (newTok && newTok !== token) {
        token = newTok;
        try {
          res = await fetch(url, { headers: buildHeaders(token), signal, ...rest });
        } catch (e) {
          if (e.name === "AbortError") throw e;
          const err = new Error(e.message || "Erro de rede");
          err.cause = e;
          throw err;
        }
      }
    } catch { /* refresh silencioso — cai no fluxo padrão de erro */ }
  }
  if (res.status === 204) return null;
  const ct = res.headers.get("content-type") || "";
  let body;
  try {
    body = ct.includes("application/json") ? await res.json() : await res.text();
  } catch (e) {
    // Se o corpo foi cancelado/disturbed (típico de StrictMode remount), reemite como AbortError
    if (signal?.aborted || /disturbed|locked|aborted/i.test(e.message)) {
      const ae = new Error("Aborted"); ae.name = "AbortError"; throw ae;
    }
    throw e;
  }
  if (!res.ok) {
    const msg = (body && body.detail) || body || `HTTP ${res.status}`;
    const err = new Error(typeof msg === "string" ? msg : JSON.stringify(msg));
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body;
}

export const api = {
  get:    (p, signal)        => request(p, { signal }),
  post:   (p, data, signal)  => request(p, { method: "POST",   body: JSON.stringify(data), signal }),
  put:    (p, data, signal)  => request(p, { method: "PUT",    body: JSON.stringify(data), signal }),
  patch:  (p, data, signal)  => request(p, { method: "PATCH",  body: JSON.stringify(data), signal }),
  del:    (p, data, signal) => request(p, { method: "DELETE", ...(data !== undefined ? { body: JSON.stringify(data) } : {}), signal }),
  // Fase 3b (Ago/2026) — upload multipart e download binário autenticados
  // (Bearer do Supabase), usados pelo Hub Documental (bucket privado).
  upload: (p, formData, signal) => requestUploadAuth(p, formData, signal),
  downloadBlob: (p, signal) => requestBlob(p, signal),
};

// ---------- Resource-specific helpers ----------
function makeResource(prefix) {
  return {
    list:   (signal)         => api.get(prefix, signal),
    get:    (id, signal)     => api.get(`${prefix}/${id}`, signal),
    create: (data, signal)   => api.post(prefix, data, signal),
    update: (id, data)       => api.put(`${prefix}/${id}`, data),
    remove: (id)             => api.del(`${prefix}/${id}`),
  };
}

export const adminPaisesAPI    = makeResource("/admin/paises");
export const adminContatoEmailsAPI = makeResource("/admin/contato-emails");
// Phase 7.31: workspaces (tenants) — CRUD + ações suspender/ativar/plano/impersonar
export const adminWorkspacesAPI = {
  list:        (signal)                  => api.get(`/admin/workspaces`, signal),
  create:      (payload)                 => api.post(`/admin/workspaces`, payload),
  patch:       (id, patch)               => api.patch(`/admin/workspaces/${id}`, patch),
  suspender:   (id)                      => api.post(`/admin/workspaces/${id}/suspender`, {}),
  ativar:      (id)                      => api.post(`/admin/workspaces/${id}/ativar`, {}),
  trocarPlano: (id, plano)               => api.patch(`/admin/workspaces/${id}/plano`, { plano }),
  impersonate: (id)                      => api.post(`/admin/workspaces/${id}/impersonate`, {}),
};

export const adminIdiomasAPI   = makeResource("/admin/idiomas");

// Phase 7.33 — Planos CRUD + Clientes (create/list) + Setup senha (público)
export const adminPlanosAPI = {
  list:   (signal)          => api.get(`/admin/planos`, signal),
  create: (payload)         => api.post(`/admin/planos`, payload),
  patch:  (id, patch)       => api.patch(`/admin/planos/${id}`, patch),
  remove: (id)              => api.del(`/admin/planos/${id}`),
};

// Phase 9.01 — V360 Planos (Porte × Nível, Matriz de Habilitação, Add-ons, Pacotes)
// Base: /admin/v360/planos/{niveis|grade|matriz-habilitacao|addons|pacotes-creditos}
const _v360Sub = (sub) => ({
  list:   (signal)         => api.get(`/admin/v360/planos/${sub}`, signal),
  create: (payload)        => api.post(`/admin/v360/planos/${sub}`, payload),
  update: (id, payload)    => api.put(`/admin/v360/planos/${sub}/${id}`, payload),
  remove: (id)             => api.del(`/admin/v360/planos/${sub}/${id}`),
});
export const adminV360PlanosAPI = {
  software:          ()              => api.get(`/admin/v360/planos/software`),
  resumo:            (signal)        => api.get(`/admin/v360/planos/resumo`, signal),
  niveis:            _v360Sub("niveis"),
  grade:             _v360Sub("grade"),
  matrizHabilitacao: _v360Sub("matriz-habilitacao"),
  addons:            _v360Sub("addons"),
  pacotesCreditos:   _v360Sub("pacotes-creditos"),
};

// Phase 9.04 — Runtime Feature Gates + admin CRUD de assinaturas V360
export const v360FeaturesAPI = {
  get: ({ empresaId, overrideNivel, signal } = {}) => {
    const qs = new URLSearchParams();
    if (empresaId) qs.set("empresa_id", empresaId);
    if (overrideNivel) qs.set("override_nivel", overrideNivel);
    const s = qs.toString();
    return api.get(`/v360/features${s ? "?" + s : ""}`, signal);
  },
};
export const adminV360AssinaturasAPI = {
  list:   (signal)                => api.get(`/admin/v360/assinaturas`, signal),
  update: (empresaId, payload)    => api.put(`/admin/v360/assinaturas/${empresaId}`, payload),
  remove: (empresaId)             => api.del(`/admin/v360/assinaturas/${empresaId}`),
};
export const adminClientesAPI = {
  list:            (signal)          => api.get(`/admin/clientes`, signal),
  get:             (workspaceId)     => api.get(`/admin/clientes/${workspaceId}`),
  create:          (payload)         => api.post(`/admin/clientes`, payload),
  update:          (workspaceId, payload) => api.patch(`/admin/clientes/${workspaceId}`, payload),
  checkNome:       (nome)            => api.get(`/admin/clientes/check-nome?nome=${encodeURIComponent(nome)}`),
  checkEmail:      (email)           => api.get(`/admin/clientes/check-email?email=${encodeURIComponent(email)}`),
  sendMagicLink:   (workspaceId)     => api.post(`/admin/clientes/${workspaceId}/send-magic-link`, {}),
  generateMagicLink: (workspaceId)   => api.post(`/admin/clientes/${workspaceId}/magic-link`, {}),
  // Item 1/2 (Out/2026) — substitui o antigo DELETE /admin/clientes/{id}
  // (descontinuado). empresaId = EmpresaPerfil.id (não o workspace_id).
  excluir:          (empresaId, motivo, forcarOverride = false) =>
    api.post(`/admin/empresas/${empresaId}/excluir`, { motivo, forcar_override: forcarOverride }),
  cancelarExclusao: (empresaId) => api.post(`/admin/empresas/${empresaId}/cancelar-exclusao`, {}),
};
export const setupPasswordAPI = {
  check:  (token)                          => api.get(`/setup-password/${token}`),
  submit: (token, senha, confirmacao)      => api.post(`/setup-password/${token}`, { senha, confirmacao }),
};
// Phase 10.29 — Magic link mock para usuários prototype (localStorage).
// Reusa a rotina de envio de e-mail do Admin, sem depender de usuário
// persistido no DB.
export const usersMockAPI = {
  sendMagicLink: ({ nome, email, workspace_nome }) =>
    api.post(`/users-mock/send-magic-link`, { nome, email, workspace_nome }),
};

// Phase 10.36 — Pergunte a Val (dashboard Sprints360).
// Phase 10.43 — aceita prompt_system e modelo_llm opcionais para permitir
// injeção do template editado pelo Admin Geral.
export const valDashboardAPI = {
  perguntar: ({ pergunta, contexto, empresa_nome, prompt_system, modelo_llm }) =>
    api.post(`/val/perguntar`, { pergunta, contexto, empresa_nome, prompt_system, modelo_llm }),
};

// Phase 10.55 — envio (mock) da Ata por e-mail aos participantes.
export const ataEmailAPI = {
  send: ({ projeto_nome, assunto, destinatarios, corpo, anexo_nome, anexo_tamanho_bytes, remetente_nome }) =>
    api.post(`/ata/send-email`, {
      projeto_nome, assunto, destinatarios, corpo, anexo_nome, anexo_tamanho_bytes, remetente_nome,
    }),
};
export const forgotPasswordAPI = {
  request: (email) => api.post(`/forgot-password`, { email }),
};
// Phase B — login agora acontece direto no Supabase Auth via
// `@/lib/supabase.js` + `supabase.auth.signInWithPassword()`. O endpoint
// legacy /api/auth/login (bcrypt) foi REMOVIDO do backend.

// Phase 7.27: cobertura de localização para selects do admin
export const adminValLocalesAPI = {
  // ?ativo=true retorna apenas idiomas habilitados; sem param retorna todos.
  coverage: (ativo, signal) => {
    const qs = (ativo === true || ativo === false) ? `?ativo=${ativo}` : "";
    return api.get(`/admin/val-locales-coverage${qs}`, signal);
  },
};
export const adminSetoresAPI   = makeResource("/admin/setores");
// Phase 12.55.10 — Matriz de Conexão: criar setor copiando aplicabilidade
adminSetoresAPI.createComCopia = (payload) => api.post("/admin/setores/copy", payload);
export const adminPortesAPI    = makeResource("/admin/portes");
export const adminEstagiosAPI  = makeResource("/admin/estagios");
export const adminFrentesAPI   = makeResource("/admin/frentes");
export const adminTopicosAPI   = makeResource("/admin/topicos");
export const adminKPIsAPI      = makeResource("/admin/kpis");
export const adminMatrizStatsAPI = () => api.get("/admin/matriz/stats");

// Matriz aplicabilidade — list paginado com filtros + update célula
export const adminMatrizAPI = {
  list: (params = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") qs.set(k, v);
    });
    const s = qs.toString();
    return api.get(`/admin/matriz${s ? "?" + s : ""}`);
  },
  updateCell: (id, valor) => api.put(`/admin/matriz/${id}`, { valor }),
};

// ADM Geral — CRUD do catálogo de Volumetrias (v360.tab_volumetria)
export const adminVolumetriasAPI = {
  list:   (signal)          => api.get("/admin/volumetrias", signal),
  get:    (id, signal)      => api.get(`/admin/volumetrias/${id}`, signal),
  create: (data, signal)    => api.post("/admin/volumetrias", data, signal),
  update: (id, patch)       => api.patch(`/admin/volumetrias/${id}`, patch),
  remove: (id)              => api.del(`/admin/volumetrias/${id}`),
};

// ============================================================
// V360 Empresa Perfil — wizard (Etapa 1/7)
// ============================================================
export const v360EmpresaAPI = {
  create:        (signal)              => api.post("/v360/empresas", undefined, signal),
  getStatusInicial: (empresaId, signal) => api.get(`/v360/empresas/${empresaId}/status-inicial`, signal),  // Phase 8.21
  getPerfil:     (empresaId, signal)   => api.get(`/v360/empresas/${empresaId}/perfil`, signal),
  // Jun/2026 — workspace (tenant) dono do perfil; usado pelo Super Admin no S360.
  getWorkspace:  (empresaId, signal)   => api.get(`/v360/empresas/${empresaId}/workspace`, signal),
  patchPerfil:   (empresaId, patch)    => api.patch(`/v360/empresas/${empresaId}/perfil`, patch),
  concluirEtapa: (empresaId, etapa)    => api.post(`/v360/empresas/${empresaId}/perfil/concluir-etapa/${etapa}`),

  // E5 — KPIs
  listKpis:            (empresaId, signal)               => api.get(`/v360/empresas/${empresaId}/kpis`, signal),
  listKpisAplicaveis:  (empresaId, signal)               => api.get(`/v360/empresas/${empresaId}/kpis-aplicaveis`, signal),
  patchKpi:            (empresaId, codigo, payload)      => api.patch(`/v360/empresas/${empresaId}/kpis/${codigo}`, payload),

  // E7 — Recomendações (E7-3: atalho = versão mais recente)
  listRecomendacoes:    (empresaId, signal)        => api.get(`/v360/empresas/${empresaId}/recomendacoes`, signal),
  // E7-3 (Fev/2026): PUT agora CRIA uma nova versão do plano (não apaga as
  // anteriores). Body: {recomendacoes, nao_cobertos, cobertura}.
  gerarNovaVersaoPlano: (empresaId, payload)       => api.put(`/v360/empresas/${empresaId}/recomendacoes`, payload),
  // E7-3 — versionamento do Plano (plano_versoes)
  listPlanoVersoes:      (empresaId, signal)         => api.get(`/v360/empresas/${empresaId}/plano-versoes`, signal),
  getLatestPlanoVersao:  (empresaId, signal)         => api.get(`/v360/empresas/${empresaId}/plano-versoes/latest`, signal),
  getPlanoVersaoRecs:    (empresaId, versaoId, signal) => api.get(`/v360/empresas/${empresaId}/plano-versoes/${versaoId}/recomendacoes`, signal),
  oficializarPlanoVersao: (empresaId, versaoId)      => api.post(`/v360/empresas/${empresaId}/plano-versoes/${versaoId}/oficializar`, {}),
  // E7-5 (Fev/2026): upsert incremental de recomendações numa versão em
  // Rascunho já existente — usado pela geração resiliente por chamada
  // (salva cada frente concluída na hora, nunca tudo-ou-nada).
  adicionarRecomendacoesVersao: (empresaId, versaoId, payload) => api.patch(`/v360/empresas/${empresaId}/plano-versoes/${versaoId}/recomendacoes`, payload),

  // E4 — Diagnóstico
  getDiagnostico:    (empresaId, signal)           => api.get(`/v360/empresas/${empresaId}/diagnostico`, signal),
  // Jun/2026 — Etapa 0 do pipeline (motor causal determinístico, read-only).
  getMatrizCausal:   (empresaId, signal)           => api.get(`/v360/empresas/${empresaId}/matriz-causal`, signal),
  // Item 3d (rodada de correção) — botão "Atualizar referências" (invalida cache de IA da Seção 4).
  atualizarReferencias: (empresaId)                => api.post(`/v360/empresas/${empresaId}/referencias/atualizar`),
  saveDiagResposta:  (empresaId, payload)          => api.post(`/v360/empresas/${empresaId}/diagnostico/respostas`, payload),

  // E3 — Volumetria (dinâmica)
  getVolumetria:     (empresaId, signal)              => api.get(`/v360/empresas/${empresaId}/volumetria`, signal),
  patchVolumetria:   (empresaId, volId, payload)      => api.patch(`/v360/empresas/${empresaId}/volumetria/${volId}`, payload),

  // Dashboard — resumo agregado (todas as etapas em uma chamada)
  getDashboard:      (empresaId, signal)              => api.get(`/v360/empresas/${empresaId}/dashboard`, signal),

  // Snapshots versionados do Dashboard (E6/E7/manual)
  listSnapshots:        (empresaId, params = {}, signal) => {
    const qs = new URLSearchParams();
    if (params.oficial !== undefined) qs.set("oficial", params.oficial);
    const s = qs.toString();
    return api.get(`/v360/empresas/${empresaId}/snapshots${s ? "?" + s : ""}`, signal);
  },
  getLatestSnapshot:    (empresaId, params = {}, signal) => {
    const qs = new URLSearchParams();
    if (params.oficial !== undefined) qs.set("oficial", params.oficial);
    const s = qs.toString();
    return api.get(`/v360/empresas/${empresaId}/snapshots/latest${s ? "?" + s : ""}`, signal);
  },
  getSnapshot:          (empresaId, snapId, signal)   => api.get(`/v360/empresas/${empresaId}/snapshots/${snapId}`, signal),
  createSnapshot:       (empresaId, payload = {})     => api.post(`/v360/empresas/${empresaId}/snapshots`, payload),
  enrichLatestSnapshot: (empresaId)                   => api.patch(`/v360/empresas/${empresaId}/snapshots/latest/recomendacoes`, {}),
  publicarSnapshot:     (empresaId, snapId)           => api.post(`/v360/empresas/${empresaId}/snapshots/${snapId}/publicar`, {}),
  // Phase 7.30: comparativo IA entre análises integradas de 2 snapshots
  compareAnalises:      (empresaId, snapAId, snapBId)  =>
    api.post(`/v360/empresas/${empresaId}/compare-analises`, { snapshot_a_id: snapAId, snapshot_b_id: snapBId }),
  deleteSnapshot:       (empresaId, snapId)           => api.del(`/v360/empresas/${empresaId}/snapshots/${snapId}`),

  // Oficialização mensal (Phase 7.4) — popup de escolha de rascunho oficial
  getOficializacaoPendente: (empresaId, signal)       => api.get(`/v360/empresas/${empresaId}/oficializacao-pendente`, signal),
  createOficializacao:      (empresaId, payload)      => api.post(`/v360/empresas/${empresaId}/oficializacao-mensal`, payload),
  listOficializacoes:       (empresaId, signal)       => api.get(`/v360/empresas/${empresaId}/oficializacao-mensal`, signal),

  // Benchmarks de mercado (Phase 7.12) — base estática armazenada
  getBenchmarks:            (empresaId, signal)       => api.get(`/v360/empresas/${empresaId}/benchmarks`, signal),

  // Phase 7.72 / 7.72.4 — Envio de e-mails com PDF anexado
  enviarRelatorio:   (payload)  => api.post(`/v360/empresas/enviar-relatorio`, payload),
  // legacy alias (compat 7.72)
  enviarDiagnostico: (payload)  => api.post(`/v360/empresas/enviar-relatorio`, { ...payload, tipo_relatorio: "diagnostico" }),
  // Phase 7.72.5 — polling de status do envio background
  getEmailLog: (empresaId, tipoRelatorio, minutes = 15, signal) => {
    const qs = new URLSearchParams();
    if (tipoRelatorio) qs.set("tipo_relatorio", tipoRelatorio);
    if (minutes)       qs.set("minutes", String(minutes));
    const q = qs.toString();
    return api.get(`/v360/empresas/${empresaId}/email-log${q ? `?${q}` : ""}`, signal);
  },
};

// Phase 7.72 — Colaboradores do workspace (Valora360LITE)
export const v360ColaboradoresAPI = {
  list:    (workspaceId, signal)   => api.get(`/v360/workspaces/${workspaceId}/colaboradores`, signal),
  convite: (payload)               => api.post(`/v360/workspaces/convidar-colaborador`, payload),
};


// Templates de prompt da Val gerenciáveis pelo ADM (Phase 7.8 + 7.26 i18n).
// Stages cobertos hoje: E3, E5, E6, E7. Cada (stage, codigo) admite múltiplas
// versões ativas — uma por idioma (BCP-47). Default = pt-BR.
export const valPromptsAPI = {
  // Público — usado pelos Stages do Wizard ao montar
  getActive: (stage, codigo, idioma, signal, versao) => {
    const params = new URLSearchParams({ stage, codigo });
    if (idioma) params.set("idioma", idioma);
    // Jun/2026 — versão explícita (corte gradual do pipeline com matriz causal).
    if (versao) params.set("versao", versao);
    return api.get(`/v360/val-prompts/active?${params.toString()}`, signal);
  },
  // Admin — filtros opcionais
  list: (stage, idioma, signal) => {
    const params = new URLSearchParams();
    if (stage) params.set("stage", stage);
    if (idioma) params.set("idioma", idioma);
    const qs = params.toString();
    return api.get(`/admin/val-prompts${qs ? `?${qs}` : ""}`, signal);
  },
  create:  (payload)       => api.post(`/admin/val-prompts`, payload),
  update:  (id, patch)     => api.patch(`/admin/val-prompts/${id}`, patch),
  remove:  (id)            => api.del(`/admin/val-prompts/${id}`),
};

/** RESTRUTURAÇÃO R/W (Out/2026, item 7) — tela simples para o Carlo ver as
 * ocorrências do log de linguagem/números por campo e regra. */
export const valSanitizacaoLogAPI = {
  list: (filtros = {}, signal) => {
    const params = new URLSearchParams();
    if (filtros.stage) params.set("stage", filtros.stage);
    if (filtros.codigo) params.set("codigo", filtros.codigo);
    if (filtros.limit) params.set("limit", filtros.limit);
    const qs = params.toString();
    return api.get(`/admin/val-sanitizacao-log${qs ? `?${qs}` : ""}`, signal);
  },
};


// ============================================================
// Central de Referências — Fontes, Execuções, Uploads (Phase 8.16)
// ============================================================
async function requestUpload(path, formData, signal) {
  const url = `${API_BASE}/api${path}`;
  const res = await fetch(url, { method: "POST", body: formData, signal });
  if (res.status === 204) return null;
  let body;
  try { body = await res.json(); } catch { body = null; }
  if (!res.ok) {
    const msg = (body && body.detail) || `HTTP ${res.status}`;
    const err = new Error(typeof msg === "string" ? msg : JSON.stringify(msg));
    err.status = res.status; err.body = body;
    throw err;
  }
  return body;
}

// Fase 3b (Ago/2026) — upload multipart autenticado (Bearer) para endpoints
// protegidos por usuário (Hub Documental / bucket privado documentos-projeto).
async function requestUploadAuth(path, formData, signal) {
  const url = `${API_BASE}/api${path}`;
  const token = await _getBearer();
  const headers = { ...(token ? { Authorization: `Bearer ${token}` } : {}), ..._impHeaders() };
  const res = await fetch(url, { method: "POST", headers, body: formData, signal });
  let body;
  try { body = await res.json(); } catch { body = null; }
  if (!res.ok) {
    const msg = (body && body.detail) || `HTTP ${res.status}`;
    const err = new Error(typeof msg === "string" ? msg : JSON.stringify(msg));
    err.status = res.status; err.body = body;
    throw err;
  }
  return body;
}

// Fase 3b — download binário autenticado (retorna Blob real, pronto para
// URL.createObjectURL). Nunca URL assinada direta da Supabase.
async function requestBlob(path, signal) {
  const url = `${API_BASE}/api${path}`;
  const token = await _getBearer();
  const headers = { ...(token ? { Authorization: `Bearer ${token}` } : {}), ..._impHeaders() };
  const res = await fetch(url, { headers, signal });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try { const body = await res.json(); msg = body.detail || msg; } catch { /* noop */ }
    const err = new Error(msg); err.status = res.status; throw err;
  }
  return await res.blob();
}

// Fase 3 (Ago/2026) — Atas de Reunião (sprints.atas). Chave lógica:
// projeto_id + evento_id (evento vive no calendário/frontend).
export const sprints360AtasAPI = {
  list:   (projetoId, signal) => api.get(`/sprints360/atas?projeto_id=${projetoId}`, signal),
  get:    (projetoId, eventoId, signal) => api.get(`/sprints360/atas/${eventoId}?projeto_id=${projetoId}`, signal),
  upsert: (eventoId, payload) => api.put(`/sprints360/atas/${eventoId}`, payload),
  remove: (projetoId, eventoId) => api.del(`/sprints360/atas/${eventoId}?projeto_id=${projetoId}`),
  // Fase 3b — gera o PDF no backend a partir dos dados já persistidos e o
  // vincula como anexo (sprints.documentos + bucket documentos-projeto).
  gerarPdf: (eventoId, projetoId) => api.post(`/sprints360/atas/${eventoId}/pdf`, { projeto_id: projetoId }),
};

// Fase 4 (Ago/2026) — Rituais recorrentes do Calendário (sprints.rituais_recorrentes).
export const sprints360RituaisRecorrentesAPI = {
  list:   (projetoId, signal) => api.get(`/sprints360/rituais-recorrentes?projeto_id=${projetoId}`, signal),
  create: (payload) => api.post(`/sprints360/rituais-recorrentes`, payload),
  update: (ritualId, patch) => api.patch(`/sprints360/rituais-recorrentes/${ritualId}`, patch),
  remove: (ritualId) => api.del(`/sprints360/rituais-recorrentes/${ritualId}`),
};

// Fase 4 (Ago/2026) — Eventos do Calendário (sprints.eventos). O GET devolve a
// mescla de ocorrências reais (avulsos + ritual materializado) e virtuais
// (ritual ainda não materializado, calculadas pelo backend).
export const sprints360EventosAPI = {
  list: (projetoId, dataInicio, dataFim, signal) =>
    api.get(`/sprints360/eventos?projeto_id=${projetoId}&data_inicio=${dataInicio}&data_fim=${dataFim}`, signal),
  create: (payload) => api.post(`/sprints360/eventos`, payload),
  upsert: (eventoId, payload) => api.put(`/sprints360/eventos/${eventoId}`, payload),
  remove: (eventoId, projetoId) => api.del(`/sprints360/eventos/${eventoId}?projeto_id=${projetoId}`),
};

// Fase 3b (Ago/2026) — Hub Documental (sprints.documentos) — bucket privado
// `documentos-projeto`. Download sempre via endpoint autenticado (streaming).
export const sprints360DocumentosAPI = {
  list: (projetoId, signal) => api.get(`/sprints360/documentos?projeto_id=${projetoId}`, signal),
  upload: (projetoId, file, categoria, descricao) => {
    const fd = new FormData();
    fd.append("projeto_id", projetoId);
    fd.append("categoria", categoria || "Diagnóstico");
    fd.append("descricao", descricao || "");
    fd.append("file", file, file.name);
    return api.upload(`/sprints360/documentos/upload`, fd);
  },
  update: (docId, projetoId, patch) => api.patch(`/sprints360/documentos/${docId}`, { projeto_id: projetoId, ...patch }),
  remove: (docId, projetoId) => api.del(`/sprints360/documentos/${docId}?projeto_id=${projetoId}`),
  download: (docId, projetoId) => api.downloadBlob(`/sprints360/documentos/${docId}/download?projeto_id=${projetoId}`),
};

export const referenciasAPI = {
  listFontes: (filtros = {}, signal) => {
    const params = new URLSearchParams();
    Object.entries(filtros).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== "") params.set(k, v);
    });
    const qs = params.toString();
    return api.get(`/admin/referencias/fontes${qs ? `?${qs}` : ""}`, signal);
  },
  getFonte:      (id, signal)     => api.get(`/admin/referencias/fontes/${id}`, signal),
  createFonte:   (payload)        => api.post(`/admin/referencias/fontes`, payload),
  patchFonte:    (id, patch)      => api.patch(`/admin/referencias/fontes/${id}`, patch),
  deleteFonte:   (id)             => api.del(`/admin/referencias/fontes/${id}`),
  testar:        (id)             => api.post(`/admin/referencias/fontes/${id}/testar`, {}),
  atualizarAgora:(id)             => api.post(`/admin/referencias/fontes/${id}/atualizar-agora`, {}),
  listExecucoes: (id, signal)     => api.get(`/admin/referencias/fontes/${id}/execucoes`, signal),
  listUploads:   (id, signal)     => api.get(`/admin/referencias/fontes/${id}/uploads`, signal),
  uploadArquivo: (id, file, observacoes, usuario) => {
    const fd = new FormData();
    fd.append("arquivo", file);
    if (observacoes) fd.append("observacoes", observacoes);
    if (usuario)     fd.append("usuario", usuario);
    return requestUpload(`/admin/referencias/fontes/${id}/upload`, fd);
  },
  deleteUpload:  (uid)            => api.del(`/admin/referencias/uploads/${uid}`),
  downloadUploadUrl: (uid)        => `${API_BASE}/api/admin/referencias/uploads/${uid}/download`,
  listPlaceholders: (signal)      => api.get(`/admin/referencias/placeholders`, signal),
};
